import { format } from "date-fns";
import type { ChangelogVersion } from "~/data/changelog-schema";
import { cn } from "~/utils/cn";

function formatMonth(iso: string): string {
	const parsed = new Date(iso);
	if (Number.isNaN(parsed.getTime())) {
		return iso;
	}
	return format(parsed, "MMM yyyy");
}

interface VersionRailProps {
	versions: ChangelogVersion[];
	selected: string | null;
	currentVersion: string;
	onSelect: (version: string) => void;
}

export const VersionRail = ({
	versions,
	selected,
	currentVersion,
	onSelect,
}: VersionRailProps) => (
	<nav aria-label="Versions" className="flex flex-col gap-1 px-1.5 py-0.5">
		{versions.map((version) => {
			const isSelected = version.version === selected;
			return (
				<button
					className={cn(
						"group relative w-full rounded-lg py-2.5 pr-3 pl-4 text-left transition-all duration-150",
						isSelected
							? "bg-background shadow-sm ring-1 ring-border/70"
							: "hover:bg-background/60"
					)}
					key={version.version}
					onClick={() => onSelect(version.version)}
					type="button"
				>
					<span
						aria-hidden
						className={cn(
							"absolute top-2.5 bottom-2.5 left-1.5 w-1 rounded-full transition-colors",
							isSelected ? "bg-primary" : "bg-transparent"
						)}
					/>
					<div className="flex items-center justify-between gap-2">
						<span
							className={cn(
								"font-semibold text-sm tabular-nums",
								isSelected
									? "text-foreground"
									: "text-muted-foreground group-hover:text-foreground"
							)}
						>
							v{version.version}
						</span>
						{version.version === currentVersion && (
							<span className="rounded-full bg-primary/10 px-1.5 py-0.5 font-semibold text-[9px] text-primary uppercase tracking-wide">
								Current
							</span>
						)}
					</div>
					<span className="mt-0.5 block text-[11px] text-muted-foreground/70">
						{formatMonth(version.date)}
					</span>
				</button>
			);
		})}
	</nav>
);
